import PropTypes from "prop-types";
import { Component } from "react";
import { initialize, propTypes, reduxForm, stopSubmit, touch, untouch } from "redux-form";
import { fields } from "./SignupSchema";
import { FormattedMessage, injectIntl } from "react-intl";
import Input from "app/pages/.shared/form/Input";
import Popover from "app/pages/.shared/Popover";
import Button from "app/pages/.shared/form/Button";
import Checkbox from "app/pages/.shared/form/Checkbox";
import FacebookContainer from "app/pages/Auth/Facebook/FacebookContainer.jsx";
import { anyFieldTouched } from "app/utils/form-validator";
import "../auth.scss";
import IconInfo from "app/pages/.shared/static/icons/IconInfo";

class Signup extends Component {
	constructor(props) {
		super(props);
		this.onFacebookError = this.onFacebookError.bind(this);
		this.onErrorClose = this.onErrorClose.bind(this);
	}

	componentDidMount() {
		this.props.initializeForm(this.props.initializeValues);
	}

	onErrorClose() {
		this.props.resetFormError();
	}

	onFacebookError() {
		this.props.stopSubmission();
	}

	render() {
		const {
			fields,
			handleSubmit,
			submitting,
			error,
			intl,
			showTopFacebook = true,
			showBottomFacebook = false,
			facebookOnSuccess,
			onToogleSigninForm,
			cnilDocumentName,
			confidentialiteDocumentName,
			signupButtonLabel = <FormattedMessage id="auth.sign.up.button.label" />,
			headline = <FormattedMessage id="auth.signup.headline" />,
			footer = null,
		} = this.props;

		return (
			<div className="auth">
				<div className="auth__main">
					<div className="auth__switch">
						<div
							className="auth__switch__item auth__switch__item--active"
							data-testid="switch-to-signUp"
						>
							<FormattedMessage id="home.signup.button.label" />
						</div>
						<div
							className="auth__switch__item"
							onClick={onToogleSigninForm}
							data-testid="switch-to-login"
						>
							<FormattedMessage id="home.signin.button.label" />
						</div>
					</div>

					{headline && <header className="auth__headline">{headline}</header>}

					{!showTopFacebook ? (
						false
					) : (
						<div className="auth__facebook">
							<FacebookContainer
								signup
								onSuccess={facebookOnSuccess}
								onError={this.onFacebookError}
							/>
						</div>
					)}

					{!showTopFacebook ? (
						false
					) : (
						<div className="auth__separator">
							<FormattedMessage id="general.ou" />
						</div>
					)}

					<form noValidate className="auth__form" onSubmit={handleSubmit}>
						<Input
							type="email"
							field={fields.email}
							label={<FormattedMessage id="auth.email.label" />}
							name="auth-email"
							id="auth-email"
							data-testid="email-input"
						/>

						<Input
							type="password"
							field={fields.password}
							label={<FormattedMessage id="auth.password.label" />}
							name="auth-password"
							id="auth-password"
							data-testid="password-input"
						/>

						<div className="auth__accept-cgv">
							<Checkbox
								field={fields.terms}
								name="accept-terms"
								id="accept-terms"
								showIcon={true}
							>
								<div className="auth__terms">
									<FormattedMessage
										id="auth.terms.label"
										values={{
											cguLink: (
												<a
													className="relative-link"
													rel="noopener noreferrer"
													target="_blank"
													href={confidentialiteDocumentName}
												>
													<FormattedMessage id="auth.terms.label.cgu.link.label" />
												</a>
											),
											confidentialiteLink: (
												<a
													className="relative-link"
													rel="noopener noreferrer"
													target="_blank"
													href={confidentialiteDocumentName}
												>
													<FormattedMessage id="auth.terms.label.confidentialite.link.label" />
												</a>
											),
										}}
									/>
								</div>
							</Checkbox>
							{cnilDocumentName && (
								<a
									rel="noopener noreferrer"
									target="_blank"
									href={cnilDocumentName}
								>
									<IconInfo width={15} height={15} />
								</a>
							)}
						</div>

						<div className="auth__action">
							<Button
								className="auth__button"
								submit
								variant="primary"
								loading={submitting}
								data-testid="signup-button"
							>
								{signupButtonLabel}
							</Button>
							<Popover
								onClose={this.onErrorClose}
								isOpen={anyFieldTouched(fields) && error}
							>
								{/* TODO passer a la version > 0.4.5 dereact-popover pour pouvoir utiliser un FormattedMessage au lieu de intl  */}
								{error ? intl.formatMessage(error) : ""}
							</Popover>

							{showBottomFacebook && (
								<div>
									<div className="auth__separator">
										<FormattedMessage id="general.ou" />
									</div>
									<div className="auth__facebook">
										<FacebookContainer
											signup
											onSuccess={facebookOnSuccess}
											onError={this.onFacebookError}
										/>
									</div>
								</div>
							)}
						</div>
					</form>
					{footer && footer.props?.children && (
						<div className="auth__footer">{footer}</div>
					)}
				</div>
			</div>
		);
	}
}

Signup.propTypes = {
	...propTypes,
	intl: PropTypes.object,
	stopSubmission: PropTypes.func,
	headline: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
	footer: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
	showTopFacebook: PropTypes.bool,
	showBottomFacebook: PropTypes.bool,
	cnilDocumentName: PropTypes.string,
	confidentialiteDocumentName: PropTypes.string,
	facebookOnSuccess: PropTypes.func,
	onToogleSigninForm: PropTypes.func,
};

const mapStateToProps = state => {
	return {
		initializeValues: {
			email: state.email,
		},
	};
};

const mapDispatchToProps = dispatch => {
	return {
		initializeForm: initializeValues => {
			dispatch(initialize("signup-form", initializeValues));
		},
		resetFormError: () => {
			dispatch(untouch("signup-form", "email", "password", "terms"));
			// Supprimer les erreurs formulaires https://github.com/erikras/redux-form/issues/2604#issuecomment-282670843
			dispatch(stopSubmit("signup-form", {}));
		},
		stopSubmission: () => {
			dispatch(touch("signup-form", "email", "password", "terms")); // pour faire apparaitre l'erreur
			dispatch(
				stopSubmit("signup-form", {
					_error: {
						id: "error.facebook.no.email",
					},
				})
			);
		},
	};
};

const reduxFormConfig = {
	form: "signup-form",
	fields,
};

export default reduxForm(reduxFormConfig, mapStateToProps, mapDispatchToProps)(injectIntl(Signup));
